import { FileSpreadsheet } from "lucide-react";
import { useFormContext } from "react-hook-form";

import type { ColocaFileUploadLabels } from "@/shared/components/forms/ColocaFileUpload";
import { ColocaFileUploadField } from "@/shared/components/forms/ColocaFileUploadField";
import useTranslations from "@/shared/lang/useTranslations";
import {
  type FileValidationFailure,
  SPREADSHEET_ACCEPT,
  validateSpreadsheetFile,
} from "@/shared/utils/fileValidation";

import { MAX_FILE_MB } from "../../constants/payoutBatches.constants";

export interface BatchFileDropzoneProps {
  disabled?: boolean;
}

/** The `file` field of the setup form: `.xlsx`/`.csv` only, 5 MB, refusals explained under the dropzone. */
export function BatchFileDropzone({ disabled }: BatchFileDropzoneProps) {
  const { t } = useTranslations("payoutBatches");
  const { setError } = useFormContext();

  const labels: ColocaFileUploadLabels = {
    instruction: t("dropzone_instruction"),
    instructionDrop: t("dropzone_instruction_drop"),
    helper: t("dropzone_helper"),
    constraints: t("dropzone_constraints", { max: MAX_FILE_MB }),
    change: t("dropzone_change"),
    remove: t("dropzone_remove"),
  };

  const reject = (result: FileValidationFailure) => {
    setError("file", {
      type: "manual",
      message:
        result.code === "too_large"
          ? t("file_error_too_large", {
              size: result.sizeMB ?? "",
              max: result.maxSizeMB ?? MAX_FILE_MB,
            })
          : t("file_error_unsupported_type"),
    });
  };

  return (
    <ColocaFileUploadField
      name="file"
      labels={labels}
      accept={SPREADSHEET_ACCEPT}
      maxSizeMB={MAX_FILE_MB}
      validate={(file) => validateSpreadsheetFile(file, MAX_FILE_MB)}
      onReject={reject}
      renderPreview={(file) => (
        <div className="flex items-center gap-3 px-4 py-3">
          <FileSpreadsheet aria-hidden="true" className="size-8 shrink-0 text-primary" />
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-foreground">{file.name}</p>
            <p className="font-mono text-xs text-muted-foreground tabular-nums">
              {t("dropzone_file_size", {
                size: Math.max(1, Math.ceil(file.size / 1024)),
              })}
            </p>
          </div>
        </div>
      )}
      disabled={disabled}
    />
  );
}
